'use client'

import { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card'
import { useUnifiedNotifications } from '@/hooks/useUnifiedNotifications'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useIframeAuth } from '@/hooks/useIframeAuth'
import { getSiteUrl } from '@/lib/auth/site-url'
import { EngageIQLogo } from '@/components/brand/EngageIQBrand'
import { destinationAfterPasswordSignIn, safeRelativeDestination } from '@/lib/auth/mfaPolicy'
import { loginErrorMessage } from '@/lib/auth/loginErrors'
import { SignedOutNotice } from '@/components/auth/SignedOutNotice'

// Google icon SVG component
function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
    )
}

/**
 * Renders test credentials only after client mount to avoid SSR hydration
 * mismatch (React #418) — and only what the SERVER says may be shown.
 *
 * Owner rule 2026-09-15: displayed login credentials show ONLY when enabled
 * AND properly configured. `/api/auth/demo-credentials` decides both (the
 * deployment switch + platform setting; the seeded identity existing here,
 * under this environment's address, confirmed, not banned) and returns the
 * accounts it could prove. This component never derives an address itself,
 * has no host-name bypass and no NODE_ENV bypass: before 2026-09-15 it showed
 * `officer@production.neutralaiz.com` on prod while auth held
 * `officer@test.neutralaiz.com`, and the click "did nothing".
 */
function TestCredentials({ onSelect }: { onSelect: (email: string, password: string) => void }) {
    const [show, setShow] = useState(false)
    const [accounts, setAccounts] = useState<string[]>([])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch('/api/auth/demo-credentials', { cache: 'no-store', credentials: 'same-origin' })
                if (!res.ok) return
                const body = (await res.json()) as { accounts?: Array<{ label: string; email: string; password: string }> }
                const list = Array.isArray(body.accounts) ? body.accounts : []
                if (cancelled || list.length === 0) return
                setAccounts(list.map((a) => `${a.label}|${a.email}|${a.password}`))
                setShow(true)
            } catch {
                // Unreachable route = nothing proven = nothing shown.
            }
        })()
        return () => { cancelled = true }
    }, [])

    if (!show) return null

    return (
        <div data-demo-id="login-test-creds" className="w-full p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-xs font-medium text-amber-400 mb-2">&#x1F9EA; Test Credentials</p>
            <div className="space-y-1 text-xs text-amber-300/80">
                {accounts.map((acct, i) => {
                    const [label, email, pass] = acct.split('|')
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onSelect(email, pass)}
                            className="block w-full text-left font-medium hover:bg-amber-500/15 rounded px-2 py-1 transition-colors cursor-pointer"
                            title={`Sign in as ${label}`}
                        >
                            <strong>{label}:</strong> {email} / {pass}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default function LoginPage() {
    const { notify } = useUnifiedNotifications()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [googleLoading, setGoogleLoading] = useState(false)
    const [authError, setAuthError] = useState<string | null>(null)
    const searchParams = useSearchParams()
    const supabase = createClient()

    // Get optional returnTo parameter for portal context preservation
    const returnToParam = searchParams.get('returnTo')
    // One validator for every navigation on this page (iframe paths, password sign-in, Google callback):
    // it also rejects backslash, control-character and dot-segment forms that resolve off-origin.
    const returnTo = safeRelativeDestination(returnToParam)

    // Iframe authentication handling
    const {
        iframeContext,
        isInitializing: iframeInitializing,
        error: iframeError,
        isAuthenticated: iframeAuthenticated,
        notifyParentLoginRequest,
    } = useIframeAuth({
        onAuthEstablished: (userId) => {
            // Redirect after successful iframe auth
            notify.success('Authenticated successfully')
            window.location.href = returnTo
        },
        onAuthFailed: (error) => {
            // Don't show error for timeout - just proceed to login UI
            if (!error.includes('timeout')) {
                setAuthError(`Authentication failed: ${error}`)
            }
        },
    })

    // If in iframe and successfully authenticated, redirect
    useEffect(() => {
        if (iframeContext.isIframe && iframeAuthenticated) {
            window.location.href = returnTo
        }
    }, [iframeContext.isIframe, iframeAuthenticated, returnTo])

    // Check for OAuth callback errors and session expiry
    useEffect(() => {
        const error = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')
        const reason = searchParams.get('reason')

        // Handle session expiry redirect
        if (reason === 'session_expired') {
            setAuthError('Your session has expired. Please log in again.')
            // Clear reason param from URL
            const url = new URL(window.location.href)
            url.searchParams.delete('reason')
            const nextUrl = url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash
            window.history.replaceState({}, '', nextUrl)
            return
        }

        if (error || errorDescription) {
            // Q-1801 S5: the URL only selects one of our fixed messages; its text is never displayed
            setAuthError(loginErrorMessage(error))
            // Clear only error params from URL without losing other params
            const url = new URL(window.location.href)
            url.searchParams.delete('error')
            url.searchParams.delete('error_description')
            const nextUrl = url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash
            window.history.replaceState({}, '', nextUrl)
        }
    }, [searchParams])

    // If in iframe and still initializing, show loading
    if (iframeContext.isIframe && iframeInitializing) {
        return (
            <div className="flex items-center justify-center w-full">
                <Card className="w-full max-w-lg shadow-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/50">
                    <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
                        <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                        <p className="text-slate-400 text-center">Authenticating...</p>
                        <p className="text-xs text-slate-400 text-center">
                            Waiting for authentication from parent application
                        </p>
                    </CardContent>
                </Card>
            </div>
        )
    }

    // If in iframe and got an error, show request login button
    if (iframeContext.isIframe && iframeError) {
        return (
            <div className="flex items-center justify-center w-full">
                <Card className="w-full max-w-lg shadow-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/50">
                    <CardHeader className="text-center space-y-4">
                        <div className="flex justify-center">
                            <EngageIQLogo
                                theme="dark"
                                width={200}
                                height={54}
                                priority={false}
                                style={{ width: 'auto', height: 'auto' }}
                            />
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center py-6 space-y-4">
                        <AlertCircle className="h-12 w-12 text-amber-400" />
                        <h2 className="text-lg font-semibold text-white">Authentication Required</h2>
                        <p className="text-sm text-slate-400 text-center">
                            Please log in through the main application to access this content.
                        </p>
                        <Button
                            onClick={notifyParentLoginRequest}
                            className="mt-4 bg-blue-600 hover:bg-blue-500"
                        >
                            Request Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        )
    }

    const loginWithPassword = async (emailArg: string, passwordArg: string) => {
        setLoading(true)
        setAuthError(null)
        setEmail(emailArg)
        setPassword(passwordArg)

        // Attempt sign in
        const { error } = await supabase.auth.signInWithPassword({
            email: emailArg,
            password: passwordArg,
        })

        if (error) {
            notify.error(error.message)
            setLoading(false)
        } else {
            notify.success('Logged in successfully')
            // Use window.location for a full page navigation to ensure
            // server components (like Header) re-render with new auth state
            // This is more reliable than router.refresh() + router.push()
            await new Promise(resolve => setTimeout(resolve, 150))
            // Keep the ordinary sign-in destination while MFA enforcement is off. When it is on,
            // the middleware checks the officer's verified AAL and routes to /mfa if needed.
            window.location.href = destinationAfterPasswordSignIn(returnTo)
        }
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        await loginWithPassword(email, password)
    }

    const handleGoogleLogin = async () => {
        setGoogleLoading(true)
        setAuthError(null)

        // In iframe context, request parent to handle OAuth
        // Parent will open OAuth popup and pass token back via postMessage
        if (iframeContext.isIframe) {
            notifyParentLoginRequest()
            // Keep loading state for a bit, then reset if parent doesn't respond
            setTimeout(() => {
                if (document.visibilityState === 'visible') {
                    setGoogleLoading(false)
                }
            }, 5000)
            return
        }

        // Standard OAuth flow for non-iframe context
        // Pass returnTo to auth callback to preserve portal context through OAuth flow
        // Use centralized site URL utility for security (env var with fallback)
        const siteUrl = getSiteUrl()
        const callbackUrl = returnTo !== '/dashboard'
            ? `${siteUrl}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`
            : `${siteUrl}/auth/callback`

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: callbackUrl,
                queryParams: {
                    // Request access to Google Workspace domain info
                    hd: 'neutralaiz.com', // Hint to show only neutralaiz.com accounts
                    // Pre-fill Google sign-in with the email if user already typed one
                    ...(email.trim() ? { login_hint: email.trim() } : {}),
                },
            },
        })

        if (error) {
            notify.error(error.message)
            setGoogleLoading(false)
            return
        }
        if (data?.url) {
            window.location.href = data.url
            return
        }
        // Fallback if no redirect occurs (e.g., blocked navigation)
        setTimeout(() => {
            if (document.visibilityState === 'visible') {
                setGoogleLoading(false)
            }
        }, 2000)
    }

    return (
        <div className="flex items-center justify-center w-full">
            <Card className="w-full max-w-lg shadow-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/50">
                <CardHeader className="text-center space-y-4">
                    <div className="flex justify-center">
                        <EngageIQLogo
                            theme="dark"
                            width={200}
                            height={54}
                            priority={false}
                            style={{ width: 'auto', height: 'auto' }}
                        />
                    </div>
                    <CardDescription className="text-slate-400">Sign in to access the system</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    {/* A70: server ended the session for inactivity (allowlisted ?reason=idle) */}
                    <SignedOutNotice reason={searchParams.get('reason')} />

                    {/* Auth Error Alert */}
                    {authError && (
                        <div role="alert" className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-blue-400">{authError}</p>
                            </div>
                            <button
                                type="button"
                                aria-label="Dismiss message"
                                onClick={() => setAuthError(null)}
                                className="text-blue-400 hover:text-blue-300"
                            >
                                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Email/Password Form */}
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-medium text-slate-300">Email</label>
                            <Input
                                id="email"
                                type="email"
                                data-demo-id="login-email-input"
                                placeholder="officer@neutralaiz.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="bg-dark-muted border-dark-border text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="password" className="text-sm font-medium text-slate-300">Password</label>
                            <Input
                                id="password"
                                type="password"
                                data-demo-id="login-password-input"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="bg-dark-muted border-dark-border text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-demo-id="login-submit-btn"
                            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-base py-6 shadow-lg shadow-blue-600/30"
                            disabled={loading || googleLoading}
                        >
                            {loading ? 'Logging in...' : 'Login with Email'}
                        </Button>
                        {/* A77: self-service password reset (public, signed-out request page) */}
                        <div className="text-right">
                            <Link
                                href="/forgot-password"
                                data-demo-id="login-forgot-password-link"
                                className="text-sm text-blue-400 hover:text-blue-300"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </form>

                    {/* Divider */}
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="bg-slate-900 px-4 text-slate-400">or</span>
                        </div>
                    </div>

                    {/* Google SSO Button */}
                    <Button
                        type="button"
                        data-demo-id="login-google-btn"
                        onClick={handleGoogleLogin}
                        disabled={googleLoading || loading}
                        className="w-full bg-white hover:bg-gray-100 text-gray-800 font-medium py-6 border border-gray-300 shadow-sm"
                    >
                        {googleLoading ? (
                            <span className="flex items-center gap-2 text-gray-700">
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Signing in...
                            </span>
                        ) : (
                            <span className="flex items-center gap-3 text-gray-700">
                                <GoogleIcon className="h-5 w-5" />
                                Continue with Google
                            </span>
                        )}
                    </Button>
                </CardContent>

                <CardFooter className="flex flex-col items-center pt-2 pb-6 gap-3">
                    <p className="text-xs text-slate-400 text-center">
                        Contact your administrator if you need access
                    </p>

                    {/* Dev Mode Test Credentials — rendered only after mount to avoid SSR hydration mismatch */}
                    <TestCredentials onSelect={loginWithPassword} />
                </CardFooter>
            </Card>
        </div>
    )
}
